/**
 * Indicator framework types (SPEC §12.4, §12.10; ADR-0018). Pure data: an indicator *definition*
 * (code, one file per indicator under `builtins/`) declares inputs, plots and placement; an
 * indicator *instance* (plain JSON, persisted per chart pane in the panel's `chartLayout`) picks a
 * definition and holds the user's input values, style overrides, pane and visibility.
 */

/** Built-in price/volume sources. Prices are absolute integer ticks; see `sources.ts`. */
export const BAR_SOURCES = [
  "open",
  "high",
  "low",
  "close",
  "hl2",
  "hlc3",
  "ohlc4",
  "hlcc4",
  "volume",
] as const;
export type BarSource = (typeof BAR_SOURCES)[number];

/**
 * A source input's value: a `BarSource`, or another indicator's plot ("indicator on indicator"):
 * `ind:<instanceId>:<plotId>` (see `sourceRef` / `parseSourceRef`).
 */
export type SourceRef = string;

type InputBase = {
  /** Label in the settings dialog (default: the key, humanised). */
  label?: string;
  /** Settings-dialog group heading. */
  group?: string;
  /** One-line help shown as the field's tooltip. */
  hint?: string;
  /** false = left out of the legend's argument list (level tables, colours-as-numbers, flavours). */
  legend?: boolean;
};

export type IntInput = InputBase & {
  type: "int";
  default: number;
  min?: number;
  max?: number;
  step?: number;
};
export type FloatInput = InputBase & {
  type: "float";
  default: number;
  min?: number;
  max?: number;
  step?: number;
};
export type BoolInput = InputBase & { type: "bool"; default: boolean };
export type EnumInput = InputBase & {
  type: "enum";
  default: string;
  options: readonly string[];
  /** Display text per option (default: the option itself). */
  optionLabels?: Readonly<Record<string, string>>;
};
export type ColorInput = InputBase & { type: "color"; default: string };
/** A price/volume source or another indicator's output. */
export type SourceInput = InputBase & { type: "source"; default: BarSource };
/** A timeframe string (`bars/timeframe.ts`, e.g. "15m", "1h", "1D"); "" = the chart's own. */
export type TimeframeInput = InputBase & { type: "timeframe"; default: string };
/**
 * A trading session anchor: "eth" = the CME trading day from 17:00 CT (ADR-0015), "rth" = the
 * 08:30–15:00 CT regular session.
 */
export type SessionInput = InputBase & { type: "session"; default: SessionAnchor };
export type SessionAnchor = "eth" | "rth";
/** Free text (Pine `input.string` without options, `input.session`); at most `maxLength` chars. */
export type TextInput = InputBase & { type: "text"; default: string; maxLength?: number };

export type InputSpec =
  | IntInput
  | FloatInput
  | BoolInput
  | EnumInput
  | ColorInput
  | SourceInput
  | TimeframeInput
  | SessionInput
  | TextInput;

export type InputsSpec = Readonly<Record<string, InputSpec>>;

/** The runtime value type of an input spec. */
export type InputValue<S extends InputSpec> = S extends { type: "int" | "float" }
  ? number
  : S extends { type: "bool" }
    ? boolean
    : S extends { type: "session" }
      ? SessionAnchor
      : string;

export type Params<I extends InputsSpec> = { readonly [K in keyof I]: InputValue<I[K]> };

export type LineStyle = "solid" | "dashed" | "dotted";
export type MarkerShape = "triangleUp" | "triangleDown" | "circle" | "cross" | "diamond" | "square";
export type MarkerLocation = "above" | "below" | "value";

/**
 * Plot kinds (TradingView plot styles, docs/TRADINGVIEW-REFERENCE.md §8):
 * - `line`, `step` — polyline through the values (NaN breaks the line);
 * - `histogram` (thin bars) / `columns` (bar-width) from `base` to the value;
 * - `area` — line plus a fill from the line to `base`;
 * - `markers` — a shape per non-NaN bar (`location` above/below the bar or at the value);
 * - `background` — shades the whole pane column of every bar whose value is non-NaN and non-zero;
 * - `hline` — a horizontal level (its value set with `ctx.level`, not per bar).
 * Colour per bar: write a palette index (`colors`) with `PlotOut.set(i, v, colorIndex)`.
 */
export type PlotKind =
  "line" | "step" | "histogram" | "columns" | "area" | "markers" | "background" | "hline";

export type PlotSpec = {
  kind: PlotKind;
  title: string;
  /** Default colour (hex, `#rrggbb` or `#rrggbbaa`). */
  color: string;
  /** Line width in CSS px (lines, steps, areas, hlines). */
  width?: number;
  style?: LineStyle;
  /** Per-bar palette (index 0 … n−1); index 255 / unset = `color`. */
  colors?: readonly string[];
  /** Histogram / columns / area base value (default 0; area default = pane bottom when NaN). */
  base?: number;
  shape?: MarkerShape;
  location?: MarkerLocation;
  /** Marker size in CSS px. */
  size?: number;
  /** Area fill opacity (0–1). */
  fillAlpha?: number;
  /** hline default value (overridden by `ctx.level`). */
  value?: number;
  /** Hidden from the legend / data window (helper plots). */
  hideValue?: boolean;
  /** false = switched off until the user ticks it in the Style tab (TradingView's optional lines). */
  visible?: boolean;
  /**
   * Pine `offset`: draw the value computed at bar `i` at bar `i + offset` (Ichimoku spans,
   * Alligator, Fractals). Display only — values are still computed at `i`, so nothing repaints.
   * An input-dependent offset is set with `ctx.offset(id, n)`, which overrides this.
   */
  offset?: number;
  /**
   * Pine `show_last`: only the values of the last N computed bars are drawn (shifted by `offset`
   * as usual). Display only — every bar is still computed, so the legend, data window, last-value
   * badge, fills and other indicators reading the plot see all values. An input-dependent count
   * is set with `ctx.showLast(id, n)`, which overrides this.
   */
  showLast?: number;
  /**
   * Pine `display = display.none`: never drawn, not in the legend, data window or Style tab, but
   * usable by fills (Supertrend's body-middle anchor).
   */
  hidden?: boolean;
};

export type PlotsSpec = Readonly<Record<string, PlotSpec>>;

/**
 * How one computation displays a plot, chosen by the indicator's inputs in `setup`
 * (`ctx.display`): Buy/Sell Volume switches between stacked, side-by-side and delta columns.
 */
export type PlotDisplay = {
  /** Not drawn and left out of the legend, data window and scale (unused by these inputs). */
  off?: boolean;
  /** Columns: slot `index` of `of` equal slots side by side within the bar's column width. */
  slot?: { index: number; of: number };
  /**
   * Columns: drawn from plot `stackOn`'s value to that value + this plot's (stacked columns);
   * the plot keeps its own value for the legend and data window.
   */
  stackOn?: string;
};

/** A band fill between two plots or hlines of the same indicator (`fill()` in Pine). */
export type FillSpec = {
  id: string;
  title: string;
  /** Plot ids. */
  a: string;
  b: string;
  color: string;
  /** Opacity (default 0.12). */
  alpha?: number;
  /**
   * Per-bar fill colour (Pine `fill(…, color = cond ? a : b)`): palette indexed by the colour index
   * plot `a` was written with (`PlotOut.set(i, v, colorIndex)`); 255 = `color`.
   */
  colors?: readonly string[];
  /**
   * Vertical gradient (Pine `fill(p1, p2, top_value, bottom_value, top_color, bottom_color)`):
   * hidden helper plots holding, per bar, the top / bottom value (plot units) with the top / bottom
   * colour as their colour index. A point of the fill at value v is coloured by interpolating
   * between the two colours by where v lies between the two values.
   */
  gradient?: { top: string; bottom: string };
  /**
   * Pine `fill(show_last)`: only the last N bars of the band are drawn (counted on plot `a`). An
   * input-dependent count is set with `ctx.fillShowLast(id, n)`, which overrides this.
   */
  showLast?: number;
};

/**
 * Pine drawing objects (`line.new`, `label.new`, `box.new`) produced from an indicator's series at
 * draw time by `ctx.draw` (Zig Zag, Auto Fib, Pivot Points Standard, Linear Regression Channel).
 * `x` is a bar index (fractional allowed; beyond the last bar = future space), `y` is in the
 * indicator's value units (absolute ticks for `format: "price"`).
 */
export type Drawing =
  | {
      kind: "line";
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      color: string;
      width?: number;
      style?: LineStyle;
      /** Show a matching value badge on the pane's price scale for this horizontal line. */
      axisLabel?: boolean;
      /** Extend beyond the end points to the pane edge (Pine `extend.*`). */
      extend?: "none" | "left" | "right" | "both";
    }
  | {
      kind: "label";
      x: number;
      y: number;
      /** Text; `{v}` is replaced by `y` printed in the indicator's format (a price for pivots). */
      text: string;
      color: string;
      /** Horizontal anchor of the text at `x` (default "center"). */
      align?: "left" | "center" | "right";
      /** Vertical placement relative to `y` (default "middle"). */
      pos?: "above" | "below" | "middle";
      /**
       * Keep the text on screen when `x` is scrolled out of the plot: it is drawn at the nearer
       * plot edge instead (Pivot Points' current-period labels, whose line ends are usually off
       * screen). The indicator sets it only while the labelled line is in view.
       */
      pin?: boolean;
    }
  | {
      kind: "box";
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      /** Fill colour; `alpha` 0–1 (default 0.15). */
      color: string;
      alpha?: number;
      extend?: "none" | "left" | "right" | "both";
    }
  | {
      /** Pine `polyline` fill: a closed polygon through `points` ([x, y] pairs). */
      kind: "polygon";
      points: readonly (readonly [number, number])[];
      color: string;
      alpha?: number;
    }
  | {
      /** Pine `linefill`: the area between two straight lines over the same x span. */
      kind: "band";
      x0: number;
      x1: number;
      /** Line a: (x0, a0) → (x1, a1); line b: (x0, b0) → (x1, b1). */
      a0: number;
      a1: number;
      b0: number;
      b1: number;
      color: string;
      alpha?: number;
      extend?: "none" | "left" | "right" | "both";
    }
  | {
      /**
       * Pine `table`: a grid of text cells pinned to a corner / edge of the pane (screen space; no
       * bar or price coordinates). Colours are `#rrggbbaa`; null = none.
       */
      kind: "table";
      position: TablePosition;
      /** Rows top to bottom, each a list of cells left to right (null = empty cell). */
      cells: (TableCell | null)[][];
      bg: string | null;
      frame: string | null;
      border: string | null;
    };

export const TABLE_POSITIONS = [
  "top_left",
  "top_center",
  "top_right",
  "middle_left",
  "middle_center",
  "middle_right",
  "bottom_left",
  "bottom_center",
  "bottom_right",
] as const;
export type TablePosition = (typeof TABLE_POSITIONS)[number];

export type TableCell = {
  text: string;
  color: string;
  bg: string | null;
  align: "left" | "center" | "right";
};

/** The bar window a `ctx.draw` callback is asked for: visible bars and the last computed bar. */
export type DrawView = { from: number; to: number; last: number };

/**
 * How values print (legend, data window, pane axis): `price` = absolute ticks shown as a price
 * (these plots share the main price scale when overlaid), `priceDelta` = a tick distance shown in
 * price units, `volume`, `percent` (value already ×100), `number` (`precision` decimals).
 */
export type ValueFormat = "price" | "priceDelta" | "volume" | "percent" | "number";

/**
 * Indicators dialog groups, in dialog order — one primary group per indicator (audit and mapping:
 * docs/INDICATORS.md "Categories"): the usual technical-analysis families TradingView's built-ins
 * fall into, plus HELM's futures group (Order flow: exact aggressor volume) and user scripts
 * (Custom). Breadth indicators (advance/decline…) do not apply to a single futures contract.
 */
export const INDICATOR_CATEGORIES = [
  "Moving averages",
  "Trend",
  "Momentum & oscillators",
  "Volatility",
  "Bands & channels",
  "Volume",
  "Order flow",
  "Pivots & levels",
  "Bill Williams",
  "Custom",
] as const;
export type IndicatorCategory = (typeof INDICATOR_CATEGORIES)[number];

// ------------------------------------------------------------------ instances (persisted JSON)

export type PlotStyle = { visible?: boolean; color?: string; width?: number; style?: LineStyle };
export type FillStyle = { visible?: boolean; color?: string; alpha?: number };
export type InstanceStyle = {
  plots?: Record<string, PlotStyle>;
  fills?: Record<string, FillStyle>;
  /** Show values in the pane legend (TradingView "Values in status line"). Default true. */
  legendValues?: boolean;
};

/**
 * Timeframe visibility (TradingView's Visibility tab): the indicator is drawn only on bar types /
 * timeframe units that are not switched off. Absent = visible everywhere.
 */
export const VISIBILITY_UNITS = [
  "seconds",
  "minutes",
  "hours",
  "days",
  "weeks",
  "months",
  "ticks",
  "volume",
  "range",
  "renko",
] as const;
export type VisibilityUnit = (typeof VISIBILITY_UNITS)[number];
export type Visibility = Partial<Record<VisibilityUnit, boolean>>;

export const MAIN_PANE = "main";

export type IndicatorInstance = {
  /** Unique within the chart (`ema-2`); referenced by chained sources and panes. */
  id: string;
  /** Registry id of the definition (`ema`). */
  type: string;
  /** Input values by key (sanitised against the definition on load). */
  params: Record<string, unknown>;
  /** `main` (overlay on price) or a sub-pane id. */
  pane: string;
  /** The legend eye. */
  visible: boolean;
  style?: InstanceStyle;
  visibility?: Visibility;
};

export type PaneState = {
  id: string;
  /** Relative height (the main pane's is `mainWeight`). */
  weight: number;
  collapsed?: boolean;
};

/** Everything indicator-related a chart persists (`chartLayout.panes[i].indicators`). */
export type ChartIndicatorsState = {
  v: 1;
  instances: IndicatorInstance[];
  /** Sub-panes below the price pane, top to bottom. */
  panes: PaneState[];
  mainWeight: number;
  /** A maximised pane id (`main` or a sub-pane), or null. */
  maximized: string | null;
  /** Data-window readout (values of every plot at the crosshair). */
  dataWindow?: boolean;
  /**
   * The price pane's indicator legend collapsed to its first row (UI plan §6): more of the candles
   * show; the overlay offers "+N" to expand. Presentation only; absent = expanded.
   */
  legendCollapsed?: boolean;
};

/** A saved set of indicators + panes (TradingView "Save indicator template"); symbol-agnostic. */
export type IndicatorTemplate = {
  name: string;
  state: ChartIndicatorsState;
};
